import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import { Box, Stack, Drawer } from "@mui/material";
import { NAVBAR } from "../../../config";
import navConfig from "./NavConfig";
import NavbarAccount from "./NavbarAccount";
import CollapseButton from "./CollapseButton";
import { NavSectionVertical } from "../../../_components/nav-section";
import Logo from "../../../_components/Logo";
import Scrollbar from "../../../_components/Scrollbar";
import useCollapseDrawer from "../../../_helpers/hooks/useCollapseDrawer";
import useResponsive from "../../../_helpers/hooks/useResponsive";
import cssStyles from "../../../_helpers/utils/cssStyles";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

const CollapseButtonContainer = styled("div")({
  position: "fixed",
  top: 20,
  zIndex: 5555,
});

// ----------------------------------------------------------------------

interface NavbarVerticalProps {
  isOpenSidebar?: any;
  onCloseSidebar?: any;
}

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
}: NavbarVerticalProps) {
  const theme = useTheme<any>();

  const { pathname } = useLocation();

  const isDesktop = useResponsive("up", "lg");

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: "center" }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Logo />
        </Stack>

        <NavbarAccount isCollapse={isCollapse} />
      </Stack>

      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />

      {/* {!isCollapse && <NavbarDocs />} */}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {isDesktop && (
        <CollapseButtonContainer
          sx={{
            transition: (theme) =>
              theme.transitions.create("left", {
                duration: theme.transitions.duration.standard,
              }),
            ...(!isCollapse && {
              left: NAVBAR.DASHBOARD_WIDTH - 23,
            }),
            ...(isCollapse && {
              left: NAVBAR.DASHBOARD_COLLAPSE_WIDTH - 23,
            }),
          }}
        >
          <CollapseButton
            onToggleCollapse={onToggleCollapse}
            collapseClick={collapseClick}
            isCollapse={isCollapse}
          />
        </CollapseButtonContainer>
      )}
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: "dashed",
              bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme: any) => theme?.customShadows?.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
